import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { ImagePickerField } from "./ImagePickerField";

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const initialForm = { title: "", short_text: "", full_text: "", image_url: "", date: new Date().toISOString().slice(0, 10), slug: "" };
const slugify = (value) => value.toLowerCase().trim().replace(/[äöüß]/g, (c) => ({ ä: "ae", ö: "oe", ü: "ue", ß: "ss" }[c] || c)).replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

export const NewsManager = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(initialForm);

  useEffect(() => { loadNews(); }, []);

  const loadNews = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/news`);
      setNews(res.data || []);
    } catch {
      toast.error("News konnten nicht geladen werden");
    }
    setLoading(false);
  };

  const resetForm = () => setForm(initialForm);

  const saveNews = async () => {
    if (!form.title || !form.short_text || !form.full_text) return toast.error("Bitte Pflichtfelder ausfüllen");
    setSaving(true);
    try {
      const payload = { ...form, slug: form.slug || slugify(form.title) };
      if (form.id) await axios.put(`${API}/news/${form.id}`, payload, auth());
      else await axios.post(`${API}/news`, payload, auth());
      toast.success(form.id ? "News gespeichert" : "News erstellt");
      resetForm();
      loadNews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Speichern");
    }
    setSaving(false);
  };

  const deleteNews = async (id) => {
    if (!window.confirm("News wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/news/${id}`, auth());
      toast.success("News gelöscht");
      if (form.id === id) resetForm();
      loadNews();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  return (
    <div className="space-y-6" data-testid="news-manager">
      <h2 className="text-2xl font-bold text-[#0A2A43]" style={{ fontFamily: "Barlow" }}>News verwalten</h2>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100"><h3 className="font-semibold text-[#0A2A43]">{form.id ? "News bearbeiten" : "News erstellen"}</h3></div>
          <div className="p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
              <input data-testid="news-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value, slug: form.id ? form.slug : slugify(e.target.value) })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
              <input data-testid="news-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Datum *</label>
              <input data-testid="news-date-input" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Kurztext *</label>
              <textarea data-testid="news-short-text-input" value={form.short_text} onChange={(e) => setForm({ ...form, short_text: e.target.value })} rows={3} className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none" />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Volltext *</label>
              <textarea data-testid="news-full-text-input" value={form.full_text} onChange={(e) => setForm({ ...form, full_text: e.target.value })} rows={6} className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none" />
            </div>
            <ImagePickerField label="Bild" value={form.image_url} onChange={(image_url) => setForm({ ...form, image_url })} dataTestId="news-image-picker" />
            <div className="flex gap-2">
              <button data-testid="news-save-button" onClick={saveNews} disabled={saving} className="px-4 py-2 rounded-lg bg-[#0A2A43] text-white text-sm hover:bg-[#0A2A43]/90 disabled:opacity-50">{saving ? "Speichern..." : form.id ? "Änderungen speichern" : "News erstellen"}</button>
              {form.id && <button data-testid="news-cancel-button" onClick={resetForm} className="px-4 py-2 rounded-lg bg-gray-100 text-sm text-gray-700 hover:bg-gray-200">Abbrechen</button>}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="px-6 py-4 border-b border-gray-100"><h3 className="font-semibold text-[#0A2A43]">News ({news.length})</h3></div>
          <div className="p-6 space-y-4 max-h-[900px] overflow-y-auto">
            {loading ? <div className="text-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0A2A43] mx-auto"></div></div> : news.length === 0 ? <p className="text-gray-400 text-center py-12">Keine News vorhanden</p> : news.map((item) => (
              <div key={item.id} className="rounded-xl border bg-gray-50 overflow-hidden" data-testid={`news-item-${item.slug}`}>
                {item.image_url && <img src={item.image_url} alt={item.title} className="w-full h-40 object-cover" />}
                <div className="p-4">
                  <p className="text-xs text-[#D32F2F] mb-1">{new Date(item.date).toLocaleDateString("de-DE")}</p>
                  <h4 className="font-semibold text-[#0A2A43]">{item.title}</h4>
                  <p className="text-sm text-gray-600 mt-2 line-clamp-3">{item.short_text}</p>
                  <div className="flex gap-3 mt-3">
                    <button data-testid={`news-edit-${item.slug}`} onClick={() => setForm({ ...item, date: (item.date || "").slice(0, 10) })} className="text-[#0A2A43] text-sm hover:underline">Bearbeiten</button>
                    <button data-testid={`news-delete-${item.slug}`} onClick={() => deleteNews(item.id)} className="text-red-500 text-sm hover:underline">Löschen</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
